// ============================================================================
// Map Deep Link Generation
// Generates validated map URLs for itinerary item locations
// JT-INT-002: Map deep links
// ============================================================================

import { isIOS } from './installPrompt';

export interface MapLinkOptions {
  name: string;
  address?: string;
  lat?: number;
  lng?: number;
}

export interface MapLink {
  url: string;
  label: string;
  provider: 'google_maps' | 'apple_maps' | 'openstreetmap';
}

/**
 * Generate a Google Maps deep link from location data.
 * Uses coordinates when available for precision, falls back to address search.
 */
export function getGoogleMapsLink(location: MapLinkOptions): MapLink {
  let url: string;

  if (location.lat !== undefined && location.lng !== undefined) {
    // Precise coordinates pin
    const query = encodeURIComponent(location.name);
    url = `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}&query_place_name=${query}`;
  } else if (location.address) {
    // Address-based search
    const q = encodeURIComponent(`${location.name}, ${location.address}`);
    url = `https://www.google.com/maps/search/${q}`;
  } else {
    // Name-only search
    const q = encodeURIComponent(location.name);
    url = `https://www.google.com/maps/search/${q}`;
  }

  return { url, label: 'Open in Maps', provider: 'google_maps' };
}

/**
 * Generate an Apple Maps deep link from location data.
 */
export function getAppleMapsLink(location: MapLinkOptions): MapLink {
  let url: string;

  if (location.lat !== undefined && location.lng !== undefined) {
    const query = encodeURIComponent(location.name);
    url = `https://maps.apple.com/?ll=${location.lat},${location.lng}&q=${query}`;
  } else if (location.address) {
    // address= shows the place without searching, with q as its label (Apple
    // Map Links). `q=<name>, <address>` is a search, so an ambiguous name can
    // drop the pin away from the address (JAR-2520). The ll+q coordinate branch
    // above is Apple's own pin-at-coordinates form and stays as it is.
    const address = encodeURIComponent(location.address);
    const label = encodeURIComponent(location.name);
    url = `https://maps.apple.com/?address=${address}&q=${label}`;
  } else {
    const q = encodeURIComponent(location.name);
    url = `https://maps.apple.com/?q=${q}`;
  }

  return { url, label: 'Open in Maps', provider: 'apple_maps' };
}

/**
 * Whether this device opens map links in Apple Maps: an iPhone, iPad or Mac.
 * An iPhone has no geo: handler, and Apple Maps is the maps app it comes with
 * (JAR-2495). An iPad that asks for desktop sites reports Macintosh, as a Mac
 * does.
 */
export function prefersAppleMaps(): boolean {
  return /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
}

/** A maps search for free text: Apple Maps on Apple devices, Google Maps elsewhere. */
export function mapsSearchUrl(query: string): string {
  const q = encodeURIComponent(query);
  return prefersAppleMaps() ? `https://maps.apple.com/?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`;
}

/**
 * Generate the best available map deep link.
 * Prefers coordinates for precision, falls back to address then name.
 * Apple devices get Apple Maps, and everything else Google Maps.
 */
export function getMapLink(
  location: MapLinkOptions,
  provider: 'google_maps' | 'apple_maps' = prefersAppleMaps() ? 'apple_maps' : 'google_maps',
): MapLink {
  switch (provider) {
    case 'apple_maps':
      return getAppleMapsLink(location);
    case 'google_maps':
    default:
      return getGoogleMapsLink(location);
  }
}

/**
 * Check if a location has enough data to generate a useful map link.
 */
export function hasValidLocation(location: MapLinkOptions): boolean {
  return !!(location.name && (location.name.trim().length > 0));
}

// ============================================================================
// JAR-2701: open the traveller's maps APP, not a web page.
//
// A web map in a new tab is what kept an iPhone in the browser. So each device
// now gets the link that hands off to a maps app, opened in place (never a new
// tab): an iPhone or iPad picks among the apps below and the choice is
// remembered; Android gets a geo: chooser; a computer keeps the web map above.
// The <MapLink> component composes these; the logic lives here so it is pure
// and testable per platform.
// ============================================================================

/** An Android device: it has a `geo:` handler and its own maps-app chooser. */
export function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

export type MapPlatform = 'ios' | 'android' | 'web';

/** How this device opens a map: the iOS app picker, an Android `geo:` chooser,
 *  or a web map on a computer. An iPadOS device asking for desktop sites counts
 *  as iOS (`isIOS` resolves the Mac masquerade). */
export function mapPlatform(): MapPlatform {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'web';
}

export type MapsProvider = 'apple' | 'google' | 'waze';

/** The maps apps an iPhone or iPad can hand off to, in the order the picker
 *  shows them. */
export const MAPS_PROVIDERS: readonly { id: MapsProvider; label: string }[] = [
  { id: 'apple', label: 'Apple Maps' },
  { id: 'google', label: 'Google Maps' },
  { id: 'waze', label: 'Waze' },
];

/** Waze universal link: opens the Waze app when installed, its web map if not. */
function getWazeLink(location: MapLinkOptions): string {
  if (location.lat !== undefined && location.lng !== undefined) {
    return `https://waze.com/ul?ll=${location.lat}%2C${location.lng}&navigate=yes`;
  }
  const q = encodeURIComponent(location.address ? `${location.name}, ${location.address}` : location.name);
  return `https://waze.com/ul?q=${q}`;
}

/** The https link that hands off to a chosen maps app on an iPhone or iPad.
 *  These are universal links: navigated IN PLACE they open the app, and fall
 *  back to that provider's web map when the app is not installed. Opening one
 *  in a new tab is what kept it in the browser (JAR-2701). */
export function appMapUrl(location: MapLinkOptions, provider: MapsProvider): string {
  switch (provider) {
    case 'apple': return getAppleMapsLink(location).url;
    case 'google': return getGoogleMapsLink(location).url;
    case 'waze': return getWazeLink(location);
  }
}

/** Android `geo:` link. Opens the phone's default maps app, or its chooser when
 *  more than one is installed. A text query rides in `q=` so a stay with no
 *  useful position is searched by name rather than pinned at 0,0. */
export function geoMapUrl(location: MapLinkOptions): string {
  const { lat, lng, name, address } = location;
  if (lat !== undefined && lng !== undefined && (lat !== 0 || lng !== 0)) {
    return `geo:${lat},${lng}?q=${lat},${lng}(${encodeURIComponent(name)})`;
  }
  const q = encodeURIComponent(address ? `${name}, ${address}` : name);
  return `geo:0,0?q=${q}`;
}

// The maps app a traveller picked on this device. A device preference like the
// theme, so it uses a `jarvis_` key and survives sign-out (the `jt.` purge
// leaves it): the phone's maps app does not change when the account does.
const MAPS_PROVIDER_KEY = 'jarvis_maps_provider';

const isMapsProvider = (v: unknown): v is MapsProvider =>
  v === 'apple' || v === 'google' || v === 'waze';

/** The remembered maps app, or null the first time — then the picker is shown. */
export function getStoredMapsProvider(): MapsProvider | null {
  try {
    const v = localStorage.getItem(MAPS_PROVIDER_KEY);
    if (isMapsProvider(v)) return v;
  } catch { /* ignore */ }
  return null;
}

export function storeMapsProvider(provider: MapsProvider): void {
  try { localStorage.setItem(MAPS_PROVIDER_KEY, provider); } catch { /* ignore */ }
}

// A phone shares a hotel as a map link, and people paste that into a stay's
// website. It names a place and where it is, but it is not the property's
// website: a Website button on it opens a map, not the hotel's own site
// (JAR-2458). So a stay whose website is a map link shows no Website button
// (JAR-3544). This mirrors core's accommodations/maplink.go `mapLink`, the
// canonical rule; keep the two in step.
export function isMapLink(url?: string | null): boolean {
  let host: string;
  let path: string;
  try {
    const u = new URL((url || '').trim());
    host = u.hostname.toLowerCase().replace(/^www\./, '');
    path = u.pathname;
  } catch {
    return false;
  }
  if (host === 'maps.app.goo.gl' || host === 'maps.apple.com') return true;
  if (host === 'goo.gl') return path.startsWith('/maps');
  if (host.startsWith('maps.google.')) return true;
  if (host.startsWith('google.')) return path === '/maps' || path.startsWith('/maps/');
  return false;
}
